import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface BrandingOptions {
  heading_color?: string;
  text_color?: string;
  background_color?: string;
  card_background_color?: string;
  login_bg_color?: string;
  login_bg_image?: string;
  login_effects_enabled?: boolean;
  sidebar_bg_color?: string;
  border_color?: string;
  header_logo_url?: string;
  header_text?: string;
}

export interface Company {
  id: string;
  name: string;
  subdomain: string;
  logo_url: string | null;
  sidebar_logo_url: string | null;
  login_logo_url: string | null;
  favicon_url: string | null;
  app_icon_url: string | null;
  primary_color: string;
  secondary_color: string | null;
  is_active: boolean;
  custom_domain?: string | null;
  branding_options?: BrandingOptions;
}

interface TenantContextType {
  company: Company | null;
  loading: boolean;
  subdomain: string | null;
  isSuperAdminDomain: boolean;
}

const TenantContext = createContext<TenantContextType>({
  company: null,
  loading: true,
  subdomain: null,
  isSuperAdminDomain: false,
});

function detectSubdomain(): string | null {
  const hostname = window.location.hostname;
  
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    const params = new URLSearchParams(window.location.search);
    return params.get('tenant') || 'default';
  }
  
  if (hostname.endsWith('.lovable.app') || hostname.endsWith('.lovableproject.com')) {
    const params = new URLSearchParams(window.location.search);
    return params.get('tenant') || 'default';
  }
  
  const baseDomain = 'deliveryjo.com';
  if (hostname.endsWith(baseDomain)) {
    const sub = hostname.replace(`.${baseDomain}`, '');
    if (sub && sub !== hostname && sub !== 'www') {
      return sub;
    }
    return 'default';
  }
  
  return 'default';
}

function getCacheKey(subdomain: string): string {
  return `tenant-cache-${subdomain}`;
}

function getCachedCompany(subdomain: string): Company | null {
  try {
    const raw = localStorage.getItem(getCacheKey(subdomain));
    if (raw) return JSON.parse(raw) as Company;
  } catch {}
  return null;
}

function setCachedCompany(subdomain: string, company: Company) {
  try {
    localStorage.setItem(getCacheKey(subdomain), JSON.stringify(company));
  } catch {}
}

function hexToHSL(hex: string): { h: number; s: number; l: number } | null {
  try {
    hex = hex.replace('#', '');
    if (hex.length === 3) hex = hex.split('').map(c => c + c).join('');
    const r = parseInt(hex.substring(0, 2), 16) / 255;
    const g = parseInt(hex.substring(2, 4), 16) / 255;
    const b = parseInt(hex.substring(4, 6), 16) / 255;
    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    let h = 0, s = 0;
    const l = (max + min) / 2;
    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
        case g: h = ((b - r) / d + 2) / 6; break;
        case b: h = ((r - g) / d + 4) / 6; break;
      }
    }
    return {
      h: Math.round(h * 360),
      s: Math.round(s * 100),
      l: Math.round(l * 100),
    };
  } catch {
    return null;
  }
}

/** Apply full CSS variable set from a single hue+saturation, matching useColorTheme logic */
function applyFullThemeFromHSL(h: number, s: number, brightness: number = 50, secondaryHsl?: { h: number; s: number; l: number } | null) {
  const root = document.documentElement;

  const bgL   = 92 + (brightness / 100) * 14;
  const bgLc  = Math.min(bgL, 99.5);
  const cardL = Math.min(bgLc + 1, 100);
  const borderL = 80 + (brightness / 100) * 18;
  const mutedL  = 88 + (brightness / 100) * 12;

  // Light mode
  root.style.setProperty('--primary', `${h} ${s}% 58%`);
  root.style.setProperty('--primary-foreground', '0 0% 100%');
  root.style.setProperty('--ring', `${h} ${s}% 58%`);
  root.style.setProperty('--background', `${h} 20% ${bgLc.toFixed(1)}%`);
  root.style.setProperty('--foreground', `${h} 50% 10%`);
  root.style.setProperty('--card', `0 0% ${cardL.toFixed(1)}%`);
  root.style.setProperty('--card-foreground', `${h} 50% 10%`);
  root.style.setProperty('--popover', `0 0% ${cardL.toFixed(1)}%`);
  root.style.setProperty('--popover-foreground', `${h} 50% 10%`);
  if (secondaryHsl) {
    root.style.setProperty('--secondary', `${secondaryHsl.h} ${secondaryHsl.s}% ${secondaryHsl.l}%`);
    root.style.setProperty('--secondary-foreground', `${secondaryHsl.h} ${Math.min(secondaryHsl.s + 20, 100)}% 15%`);
  } else {
    root.style.setProperty('--secondary', `${h} 30% ${mutedL.toFixed(1)}%`);
    root.style.setProperty('--secondary-foreground', `${h} 50% 15%`);
  }
  root.style.setProperty('--muted', `${h} 20% ${mutedL.toFixed(1)}%`);
  root.style.setProperty('--muted-foreground', `${h} 10% 45%`);
  root.style.setProperty('--accent', `${h} 35% ${mutedL.toFixed(1)}%`);
  root.style.setProperty('--accent-foreground', `${h} 50% 15%`);
  root.style.setProperty('--border', `${h} 20% ${borderL.toFixed(1)}%`);
  root.style.setProperty('--input', `${h} 20% ${borderL.toFixed(1)}%`);
  root.style.setProperty('--sidebar-background', `${h} 25% ${Math.min(bgLc + 1.5, 100).toFixed(1)}%`);
  root.style.setProperty('--sidebar-foreground', `${h} 30% 20%`);
  root.style.setProperty('--sidebar-primary', `${h} ${s}% 58%`);
  root.style.setProperty('--sidebar-primary-foreground', '0 0% 100%');
  root.style.setProperty('--sidebar-accent', `${h} 30% ${mutedL.toFixed(1)}%`);
  root.style.setProperty('--sidebar-accent-foreground', `${h} 40% 15%`);
  root.style.setProperty('--sidebar-border', `${h} 15% ${borderL.toFixed(1)}%`);
  root.style.setProperty('--sidebar-ring', `${h} ${s}% 58%`);
  root.style.setProperty('--chart-1', `${h} 70% 55%`);
  root.style.setProperty('--chart-2', `${h + 22} 60% 50%`);
  root.style.setProperty('--chart-3', `${h - 28} 55% 55%`);

  // Dark mode
  const darkBgL   = 3 + (brightness / 100) * 10;
  const darkCardL = darkBgL + 2;
  const darkBorderL = darkBgL + 10;
  const darkMutedL  = darkBgL + 9;

  const styleId = 'dynamic-dark-theme';
  let styleEl = document.getElementById(styleId) as HTMLStyleElement | null;
  if (!styleEl) {
    styleEl = document.createElement('style');
    styleEl.id = styleId;
    document.head.appendChild(styleEl);
  }
  styleEl.textContent = `
    .dark {
      --primary: ${h} ${s}% 62% !important;
      --primary-foreground: 0 0% 100% !important;
      --ring: ${h} ${s}% 62% !important;
      --background: ${h} 30% ${darkBgL.toFixed(1)}% !important;
      --foreground: ${h} 10% 95% !important;
      --card: ${h} 30% ${darkCardL.toFixed(1)}% !important;
      --card-foreground: ${h} 10% 95% !important;
      --popover: ${h} 30% ${darkCardL.toFixed(1)}% !important;
      --popover-foreground: ${h} 10% 95% !important;
      --secondary: ${secondaryHsl ? `${secondaryHsl.h} ${Math.max(secondaryHsl.s - 10, 0)}% ${Math.max(secondaryHsl.l - 40, 10)}%` : `${h} 25% ${darkMutedL.toFixed(1)}%`} !important;
      --secondary-foreground: ${secondaryHsl ? `${secondaryHsl.h} 10% 95%` : `${h} 10% 95%`} !important;
      --muted: ${h} 25% ${darkMutedL.toFixed(1)}% !important;
      --muted-foreground: ${h} 10% 60% !important;
      --accent: ${h} 25% ${darkMutedL.toFixed(1)}% !important;
      --accent-foreground: ${h} 10% 95% !important;
      --border: ${h} 20% ${darkBorderL.toFixed(1)}% !important;
      --input: ${h} 20% ${darkBorderL.toFixed(1)}% !important;
      --sidebar-background: ${h} 30% ${darkCardL.toFixed(1)}% !important;
      --sidebar-foreground: ${h} 10% 90% !important;
      --sidebar-primary: ${h} ${s}% 62% !important;
      --sidebar-primary-foreground: 0 0% 100% !important;
      --sidebar-accent: ${h} 20% ${(darkBgL + 6).toFixed(1)}% !important;
      --sidebar-accent-foreground: ${h} 10% 90% !important;
      --sidebar-border: ${h} 20% ${(darkBgL + 6).toFixed(1)}% !important;
      --sidebar-ring: ${h} ${s}% 62% !important;
    }
  `;
}

function applyCompanyBranding(company: Company) {
  if (!company) return;
  
  document.title = company.name || 'DeliveryJo';
  
  // Favicon
  if (company.favicon_url) {
    let link = document.querySelector("link[rel~='icon']") as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = company.favicon_url;
  }
  
  // Apply full theme from primary color (+ optional secondary)
  if (company.primary_color) {
    const hsl = hexToHSL(company.primary_color);
    const secondaryHsl = company.secondary_color ? hexToHSL(company.secondary_color) : null;
    if (hsl) {
      applyFullThemeFromHSL(hsl.h, hsl.s, 50, secondaryHsl);
      try { localStorage.setItem('tenant-has-custom-theme', 'true'); } catch {}
    }

    // Update theme-color meta (controls PWA top bar color)
    const themeMeta = document.querySelector('meta[name="theme-color"]');
    if (themeMeta) themeMeta.setAttribute('content', company.primary_color);
  }

  // Apply advanced branding overrides
  const bo = company.branding_options;
  if (bo) {
    const root = document.documentElement;
    if (bo.text_color) {
      const hsl = hexToHSL(bo.text_color);
      if (hsl) root.style.setProperty('--foreground', `${hsl.h} ${hsl.s}% ${hsl.l}%`);
    }
    if (bo.heading_color) {
      const hsl = hexToHSL(bo.heading_color);
      if (hsl) {
        const val = `${hsl.h} ${hsl.s}% ${hsl.l}%`;
        root.style.setProperty('--primary', val);
        root.style.setProperty('--ring', val);
        root.style.setProperty('--sidebar-primary', val);
        root.style.setProperty('--card-foreground', val);
        root.style.setProperty('--popover-foreground', val);
      }
    }
    if (bo.background_color) {
      const hsl = hexToHSL(bo.background_color);
      if (hsl) root.style.setProperty('--background', `${hsl.h} ${hsl.s}% ${hsl.l}%`);
    }
    if (bo.card_background_color) {
      const hsl = hexToHSL(bo.card_background_color);
      if (hsl) {
        root.style.setProperty('--card', `${hsl.h} ${hsl.s}% ${hsl.l}%`);
        root.style.setProperty('--popover', `${hsl.h} ${hsl.s}% ${hsl.l}%`);
      }
    }
    if (bo.border_color) {
      const hsl = hexToHSL(bo.border_color);
      if (hsl) {
        root.style.setProperty('--border', `${hsl.h} ${hsl.s}% ${hsl.l}%`);
        root.style.setProperty('--input', `${hsl.h} ${hsl.s}% ${hsl.l}%`);
      }
    }
    if (bo.sidebar_bg_color) {
      const hsl = hexToHSL(bo.sidebar_bg_color);
      if (hsl) root.style.setProperty('--sidebar-background', `${hsl.h} ${hsl.s}% ${hsl.l}%`);
    }
    // Store login branding as CSS custom properties
    if (bo.login_bg_color) {
      root.style.setProperty('--login-bg-color', bo.login_bg_color);
    }
    if (bo.login_bg_image) {
      root.style.setProperty('--login-bg-image', `url(${bo.login_bg_image})`);
    }
    root.style.setProperty('--login-effects-enabled', bo.login_effects_enabled === false ? '0' : '1');
  }

  // Update apple-mobile-web-app-title
  if (company.name) {
    const appleTitleMeta = document.querySelector('meta[name="apple-mobile-web-app-title"]');
    if (appleTitleMeta) appleTitleMeta.setAttribute('content', company.name);
  }

  // Dynamic PWA manifest
  applyDynamicManifest(company);

  // Store primary color as CSS custom property for login screen
  if (company.primary_color) {
    document.documentElement.style.setProperty('--tenant-primary-hex', company.primary_color);
  }
}

function applyDynamicManifest(company: Company) {
  const iconUrl = company.app_icon_url || company.favicon_url || company.logo_url;
  
  const manifest = {
    name: company.name,
    short_name: company.name,
    description: company.name,
    theme_color: company.primary_color || '#0f1a2e',
    background_color: company.primary_color || '#0f1a2e',
    display: 'standalone',
    orientation: 'portrait',
    start_url: '/',
    icons: iconUrl ? [
      { src: iconUrl, sizes: '192x192', type: 'image/png' },
      { src: iconUrl, sizes: '512x512', type: 'image/png' },
      { src: iconUrl, sizes: '512x512', type: 'image/png', purpose: 'any maskable' },
    ] : [],
  };

  const blob = new Blob([JSON.stringify(manifest)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);

  // Remove existing manifest links
  document.querySelectorAll('link[rel="manifest"]').forEach(el => el.remove());

  const link = document.createElement('link');
  link.rel = 'manifest';
  link.href = url;
  document.head.appendChild(link);

  // Also update apple-touch-icon
  if (iconUrl) {
    let appleLink = document.querySelector("link[rel='apple-touch-icon']") as HTMLLinkElement | null;
    if (!appleLink) {
      appleLink = document.createElement('link');
      appleLink.rel = 'apple-touch-icon';
      document.head.appendChild(appleLink);
    }
    appleLink.href = iconUrl;
  }
}

// === Immediate branding from cache (runs before any React render) ===
const _earlySubdomain = detectSubdomain();
if (_earlySubdomain && _earlySubdomain !== 'admin' && _earlySubdomain !== 'super') {
  const cached = getCachedCompany(_earlySubdomain);
  if (cached) {
    applyCompanyBranding(cached);
  }
}

export const TenantProvider = ({ children }: { children: ReactNode }) => {
  const subdomain = _earlySubdomain;
  const isSuperAdminDomain = subdomain === 'admin' || subdomain === 'super';
  const cachedCompany = subdomain && !isSuperAdminDomain ? getCachedCompany(subdomain) : null;
  const [company, setCompany] = useState<Company | null>(cachedCompany);
  const [loading, setLoading] = useState(!cachedCompany);

  useEffect(() => {
    if (!subdomain || isSuperAdminDomain) {
      // Clear custom theme flag for non-tenant domains
      try { localStorage.removeItem('tenant-has-custom-theme'); } catch {}
      setLoading(false);
      return;
    }

    const fetchCompany = async () => {
      try {
        const { data, error } = await supabase
          .from('companies_public')
          .select('id, name, subdomain, custom_domain, logo_url, sidebar_logo_url, login_logo_url, favicon_url, app_icon_url, primary_color, secondary_color, branding_options, is_active')
          .eq('subdomain', subdomain)
          .maybeSingle();

        if (!error && data) {
          const comp = data as unknown as Company;
          setCompany(comp);
          setCachedCompany(subdomain, comp);
          applyCompanyBranding(comp);
        }
      } catch (err) {
        console.error('Failed to load tenant:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchCompany();
  }, [subdomain]);

  return (
    <TenantContext.Provider value={{ company, loading, subdomain, isSuperAdminDomain }}>
      {children}
    </TenantContext.Provider>
  );
};

export const useTenant = () => useContext(TenantContext);

/** Check if tenant has custom branding applied */
export function hasTenantCustomTheme(): boolean {
  try {
    return localStorage.getItem('tenant-has-custom-theme') === 'true';
  } catch {
    return false;
  }
}
